import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { recordCallJoin } from '../../services/attendanceService';
import {
  MEETING_PROVIDERS,
  getMeetingProviderLabel,
  getSavedMeetingLinks,
  resolveMeetingJoinUrl,
} from '../../utils/meetingUtils';

const ConnectMeetingModal = ({ isOpen, onClose, channelId, onMeetingStarted }) => {
  const [provider, setProvider] = useState('zoom');
  const [meetingInput, setMeetingInput] = useState('');
  const [label, setLabel] = useState('');
  const [savedLinks, setSavedLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setProvider('zoom');
    setMeetingInput('');
    setLabel('');
    setError(null);

    const loadProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSavedLinks(getSavedMeetingLinks(response.data?.socialLinks));
      } catch {
        setSavedLinks([]);
      }
    };
    loadProfile();
  }, [isOpen]);

  const resolvedUrl = useMemo(
    () => resolveMeetingJoinUrl(provider, meetingInput),
    [provider, meetingInput],
  );

  const handleSelectSavedLink = (link) => {
    setProvider(link.provider || 'custom');
    setMeetingInput(link.url);
    if (!label.trim()) {
      setLabel(link.label);
    }
  };

  const handleStart = async () => {
    const joinUrl = resolvedUrl || meetingInput.trim();
    if (!joinUrl) {
      setError('Enter a valid meeting ID or link.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/messages/channel/${channelId}/meeting`,
        {
          joinUrl,
          provider,
          label: label.trim() || getMeetingProviderLabel(provider),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      window.open(joinUrl, '_blank', 'noopener,noreferrer');
      recordCallJoin({
        title: label.trim() || getMeetingProviderLabel(provider),
        channelId,
        messageId: response.data?.id,
        provider,
        liveSession: false,
      }).catch(() => {});
      onMeetingStarted?.(response.data);
      onClose();
    } catch (err) {
      const msg = err.response?.data;
      const text = typeof msg === 'string'
        ? msg.replace(/^Error:\s*/i, '')
        : msg?.message || 'Failed to start meeting';
      setError(text);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md border border-gray-200 dark:border-gray-700"
        role="dialog"
        aria-modal="true"
        aria-labelledby="connect-meeting-title"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id="connect-meeting-title" className="text-lg font-semibold text-gray-900 dark:text-white">
            Connect — Start meeting
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Platform
            </label>
            <div className="grid grid-cols-2 gap-2">
              {MEETING_PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProvider(p.id)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    provider === p.id
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="meeting-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {provider === 'custom' ? 'Meeting link' : 'Meeting ID or link'}
            </label>
            <input
              id="meeting-input"
              type="text"
              value={meetingInput}
              onChange={(e) => setMeetingInput(e.target.value)}
              placeholder={
                provider === 'zoom' ? 'e.g. 1234567890 or full Zoom URL'
                  : provider === 'google_meet' ? 'e.g. abc-defg-hij or meet.google.com/...'
                  : provider === 'teams' ? 'Paste full Teams meeting link'
                  : 'https://...'
              }
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
            />
            {resolvedUrl && resolvedUrl !== meetingInput.trim() && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate" title={resolvedUrl}>
                Will open: {resolvedUrl}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="meeting-label" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Display name (optional)
            </label>
            <input
              id="meeting-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Sprint planning"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
            />
          </div>

          {savedLinks.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Your saved links
              </p>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {savedLinks.map((link) => (
                  <button
                    key={link.id}
                    type="button"
                    onClick={() => handleSelectSavedLink(link)}
                    className="w-full text-left px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 dark:text-white">{link.label}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{link.url}</span>
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                Manage links in Profile → Social links
              </p>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleStart}
            disabled={loading || !meetingInput.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded-lg"
          >
            {loading ? 'Starting…' : 'Start & join'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectMeetingModal;
